import { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowDown, ArrowUpRight, Clock, MapPin, Users, Layers } from "lucide-react";
import HeroPromptInput from "@/components/HeroPromptInput";
import Seo, { localBusinessLd, faqLd } from "@/lib/seo";
import { VILLES, TERRITOIRES, zoneUrl } from "@/constants/zones";
import { GUIDES, guideUrl } from "@/constants/guides";

const HOME_FAQ = [
  {
    q: "Dans quelles villes RAMA DÉCO intervient-il ?",
    a: "Nous sommes basés à Villepinte (93420) et intervenons à Paris, dans toute la Seine-Saint-Denis, à l'est du Val-d'Oise et au nord-ouest de la Seine-et-Marne. Le déplacement et le devis sont gratuits.",
  },
  {
    q: "Quels travaux réalisez-vous ?",
    a: "Tous les travaux intérieurs et de second œuvre : peinture, plâtrerie et cloisons, sols et carrelage, électricité et plomberie intérieures, salles de bains, cuisines, menuiseries intérieures, isolation intérieure et finitions. Nous n'intervenons pas sur le gros œuvre.",
  },
  {
    q: "Comment obtenir une estimation de mes travaux ?",
    a: "Décrivez votre projet en ligne, à l'écrit ou à la voix. Notre estimateur identifie les lots concernés et vous renvoie une fourchette de budget ventilée par poste en cinq minutes, avant toute visite.",
  },
  {
    q: "Le devis est-il gratuit ?",
    a: "Oui. La visite technique, les conseils et le devis détaillé sont gratuits et sans engagement, partout dans notre zone d'intervention.",
  },
];

const METIERS = [
  "Peinture & préparation","Placo, cloisons & plafonds","Sols, parquet & carrelage",
  "Électricité intérieure","Plomberie intérieure","Salle de bains",
  "Cuisine & agencement","Menuiserie intérieure","Isolation intérieure",
  "Dépose non structurelle","Finitions","Rénovation intérieure complète",
];

// Photo intérieure premium — salon rénové, grande lumière naturelle
const HERO_IMG = "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=85";
const HERO_IMG_SM = "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&auto=format&fit=crop&w=900&q=80";

const REASSURANCE = [
  { icon: MapPin, t: "Devis et déplacement gratuits", d: "Dans toute l'Île-de-France." },
  { icon: Clock, t: "Une estimation en 5 minutes", d: "Expliquez votre projet avec vos propres mots." },
  { icon: Users, t: "Plusieurs métiers, un interlocuteur", d: "Peinture, placo, sols, électricité, plomberie et finitions." },
  { icon: Layers, t: "Rénovation et bâtiments neufs", d: "Pour les travaux intérieurs et les lots de second œuvre." },
];

export default function HomePage() {
  const estimatorRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    document.documentElement.classList.add("js-reveal");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => e.isIntersecting && e.target.classList.add("in"));
    }, { threshold: 0.05, rootMargin: "0px 0px 10% 0px" });
    document.querySelectorAll(".reveal").forEach(el => obs.observe(el));
    const safety = setTimeout(() => document.querySelectorAll(".reveal").forEach(el => el.classList.add("in")), 3000);
    return () => { clearTimeout(safety); obs.disconnect(); document.documentElement.classList.remove("js-reveal"); };
  }, []);

  const scrollToEstimator = () => {
    const el = estimatorRef.current;
    if (!el) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    setTimeout(() => {
      const title = el.querySelector("[data-focus-target]");
      if (title) title.setAttribute("tabindex", "-1"), title.focus({ preventScroll: true });
    }, reduce ? 100 : 900);
  };

  return (
    <div>
      <Seo
        title="RAMA DÉCO — Entreprise de rénovation & travaux intérieurs à Villepinte, Paris et Île-de-France"
        description="Entreprise de travaux intérieurs, rénovation et second œuvre à Villepinte (93). Peinture, placo, sols, salles de bains, électricité. Estimation en ligne en 5 minutes, devis gratuit en Île-de-France."
        path="/"
        jsonLd={[
          localBusinessLd([...TERRITOIRES, ...VILLES].map((z) => z.name)),
          faqLd(HOME_FAQ),
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "RAMA DÉCO",
            url: "https://www.ramadeco.fr",
            inLanguage: "fr-FR",
          },
        ]}
      />
      {/* CINEMATIC HERO — plein écran */}
      <section
        data-testid="cinematic-hero"
        className="relative w-full overflow-hidden bg-anthracite"
        style={{ minHeight: "min(100svh, 900px)" }}
      >
        {/* Image + masque architectural */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            srcSet={`${HERO_IMG_SM} 900w, ${HERO_IMG} 2400w`}
            sizes="100vw"
            alt="Intérieur rénové par RAMA DÉCO"
            fetchPriority="high"
            width="2400"
            height="1600"
            className="w-full h-full object-cover hero-image-mask"
          />
          {/* Voile bordeaux/anthracite pour lisibilité */}
          <div className="absolute inset-0 bg-gradient-to-b from-anthracite/70 via-anthracite/40 to-anthracite/85" />
          <div className="absolute inset-0 bg-gradient-to-r from-anthracite/70 via-transparent to-transparent" />
          {/* Fines lignes de plan */}
          <svg className="absolute inset-0 w-full h-full opacity-25 hero-plan-lines pointer-events-none" preserveAspectRatio="none" viewBox="0 0 1600 900">
            <line x1="0" y1="640" x2="1600" y2="640" stroke="#F2EFEA" strokeWidth="1" strokeDasharray="6 8" />
            <line x1="1180" y1="0" x2="1180" y2="900" stroke="#F2EFEA" strokeWidth="1" strokeDasharray="6 8" />
            <line x1="60" y1="120" x2="60" y2="900" stroke="#F2EFEA" strokeWidth="1" />
            <circle cx="60" cy="640" r="4" fill="#F2EFEA" />
            <circle cx="1180" cy="640" r="4" fill="#F2EFEA" />
          </svg>
        </div>

        {/* Contenu hero */}
        <div className="relative wrap flex flex-col md:justify-center min-h-[100svh] pt-[112px] md:pt-[140px] pb-24 md:pb-32">
          <div className="max-w-3xl">
            <div className="hero-reveal font-mono-caption text-ivory/80 mb-6" style={{ animationDelay: "60ms" }}>
              Rénovation · Travaux intérieurs · Second œuvre
            </div>

            <h1 className="hero-reveal font-display text-5xl sm:text-6xl lg:text-[5.6rem] leading-[0.98] tracking-tight text-ivory" style={{ animationDelay: "220ms" }}>
              Vos travaux{" "}
              <br className="hidden sm:block" />
              <span className="italic">prennent forme</span>{" "}
              <br className="hidden sm:block" />
              dès maintenant.
            </h1>

            <p className="hero-reveal mt-8 max-w-xl text-ivory/85 text-base md:text-lg leading-relaxed" style={{ animationDelay: "380ms" }}>
              Décrivez simplement votre projet. RAMA DÉCO identifie les travaux nécessaires,
              vous donne une première estimation et vous accompagne jusqu'à la visite du chantier.
            </p>

            {/* Preuve 5 min */}
            <div className="hero-reveal mt-8 inline-flex items-center gap-3 px-4 py-2.5 rounded-full bg-ivory/12 backdrop-blur-sm border border-ivory/25 text-ivory" style={{ animationDelay: "500ms" }}>
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ivory/75 opacity-70"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-ivory"></span>
              </span>
              <span className="text-sm font-medium">Une estimation réelle de votre projet en 5 min.</span>
            </div>

            <div className="hero-reveal mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4" style={{ animationDelay: "620ms" }}>
              <button
                data-testid="hero-cta-estimer"
                onClick={scrollToEstimator}
                className="group inline-flex items-center gap-3 bg-bordeaux-700 hover:bg-bordeaux-900 text-ivory px-7 py-4 rounded-rama-sm font-semibold text-[0.95rem] tracking-wide shadow-[0_18px_50px_-15px_rgba(107,14,26,0.75)] transition-all duration-300 hover:-translate-y-0.5"
              >
                Estimer mon projet
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <a
                href="tel:0627443230"
                data-testid="hero-secondary-appel"
                className="inline-flex items-center gap-2 text-ivory/90 hover:text-ivory link-underline text-sm font-semibold tracking-wide"
              >
                Parler à un conducteur de travaux <ArrowUpRight size={16} />
              </a>
            </div>
          </div>

          {/* Indication scroll */}
          <button
            onClick={scrollToEstimator}
            aria-label="Défiler vers l'estimateur"
            className="hero-reveal absolute inset-x-0 mx-auto w-fit bottom-8 md:bottom-10 flex flex-col items-center gap-2 text-ivory/70 hover:text-ivory transition-colors"
            style={{ animationDelay: "820ms" }}
          >
            <span className="font-mono-caption text-[0.62rem] tracking-[0.24em]">Défiler</span>
            <ArrowDown size={16} className="animate-bounce" style={{ animationDuration: "2.4s" }} />
          </button>
        </div>
      </section>

      {/* =========================================
          BANDEAU DE RÉASSURANCE
          ========================================= */}
      <section data-testid="reassurance-band" className="relative bg-ivory border-b border-black/10 scroll-mt-24 pt-4 md:pt-0">
        <div className="wrap grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-black/8">
          {REASSURANCE.map((r, i) => (
            <div key={i} data-testid={`reassurance-${i}`} className="p-6 md:p-8 flex flex-col gap-2 reveal">
              <r.icon size={20} className="text-bordeaux-700" strokeWidth={1.6} />
              <div className="font-display text-base md:text-lg text-anthracite leading-tight mt-1">{r.t}</div>
              <div className="text-xs md:text-sm text-ink-soft leading-snug">{r.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* =========================================
          TRANSITION VERS ESTIMATEUR
          ========================================= */}
      <section ref={estimatorRef} data-testid="estimator-transition" className="section pt-24 md:pt-32 scroll-mt-24">
        <div className="wrap grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5 reveal">
            <div className="font-mono-caption text-bordeaux-700 mb-4">Votre projet commence ici</div>
            <h2 data-focus-target tabIndex={-1} className="font-display text-3xl md:text-5xl tracking-tight text-anthracite leading-[1.05] focus:outline-none">
              Parlez-nous de ce que vous souhaitez <span className="italic text-bordeaux-700">transformer.</span>
            </h2>
            <p className="mt-6 text-ink-soft text-base md:text-lg leading-relaxed max-w-lg">
              Pas besoin de connaître les termes techniques. Décrivez simplement votre logement,
              votre local ou les travaux souhaités.
            </p>
            <div className="mt-6 text-sm text-ink-soft">
              Vous préférez sélectionner vos travaux ?{" "}
              <Link to="/catalogue" data-testid="link-catalogue" className="link-underline text-anthracite font-semibold">Choisir mes travaux →</Link>
            </div>
          </div>
          <div className="lg:col-span-7 reveal">
            <HeroPromptInput />
          </div>
        </div>
      </section>

      {/* =========================================
          COMMENT ÇA MARCHE
          ========================================= */}
      <section className="section reveal">
        <div className="wrap grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="font-mono-caption text-ink-soft mb-4">Comment ça marche</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-anthracite leading-tight">Décrivez, comprenez, décidez.</h2>
            <p className="mt-4 text-ink-soft max-w-md">Le vocabulaire technique, c'est notre affaire. Vous parlez normalement, RAMA analyse.</p>
            <Link to="/comment-ca-marche" data-testid="link-comment" className="mt-6 inline-flex items-center gap-2 text-anthracite font-semibold link-underline">Voir les 7 étapes <ArrowUpRight size={16} /></Link>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-4">
            {[
              { n: "01", t: "Décrivez votre projet", d: "Texte libre ou dictée vocale. Pas de vocabulaire technique nécessaire." },
              { n: "02", t: "RAMA analyse les travaux", d: "Détection des lots intérieurs : préparation, finitions, second œuvre." },
              { n: "03", t: "Obtenez votre estimation", d: "Une fourchette claire, ventilée par postes. Pas de faux prix précis." },
              { n: "04", t: "Planifiez la visite", d: "Créneau au choix : visite technique sur site ou appel préalable." },
            ].map(s => (
              <div key={s.n} className="p-6 bg-white border border-black/10 rounded-rama-md hover:border-bordeaux-700 transition-colors">
                <div className="font-mono-caption text-bordeaux-700 mb-3">{s.n}</div>
                <div className="font-display text-xl text-anthracite mb-2">{s.t}</div>
                <div className="text-sm text-ink-soft leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          MÉTIERS
          ========================================= */}
      <section className="section bg-greige/30 reveal">
        <div className="wrap">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
            <div>
              <div className="font-mono-caption text-ink-soft mb-3">Nos métiers intérieurs</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight text-anthracite">Plusieurs corps d'état.{" "}<br/><span className="italic text-bordeaux-700">Un interlocuteur.</span></h2>
            </div>
            <Link to="/nos-metiers" data-testid="link-metiers" className="btn-ghost self-start">Explorer les métiers <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-black/10 border border-black/10 rounded-rama-md overflow-hidden">
            {METIERS.map((m, i) => (
              <div key={m} className="bg-ivory p-6 md:p-8 group hover:bg-white transition-colors">
                <div className="font-mono-caption text-ink-soft mb-4">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-display text-lg md:text-xl text-anthracite group-hover:text-bordeaux-700 transition-colors">{m}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          FAQ — visible + données structurées
          ========================================= */}
      <section data-testid="home-faq" className="section reveal">
        <div className="wrap grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="font-mono-caption text-bordeaux-700 mb-4">Questions fréquentes</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-anthracite leading-tight">
              Les réponses aux questions <span className="italic">qu'on nous pose le plus.</span>
            </h2>
            <Link to="/contact" data-testid="home-faq-contact" className="mt-6 inline-flex items-center gap-2 text-anthracite font-semibold link-underline">
              Poser votre question <ArrowUpRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-8 divide-y divide-black/10 border-y border-black/10">
            {HOME_FAQ.map((f, i) => (
              <details key={i} data-testid={`home-faq-${i}`} className="group py-5">
                <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
                  <h3 className="font-display text-lg md:text-xl text-anthracite leading-snug">{f.q}</h3>
                  <span className="text-bordeaux-700 shrink-0 mt-1 transition-transform duration-300 group-open:rotate-45 text-xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm md:text-base text-ink-soft leading-relaxed max-w-3xl">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          ZONES D'INTERVENTION — SEO LOCAL
          ========================================= */}
      <section data-testid="home-zones" className="section bg-greige/30 reveal">
        <div className="wrap">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <div className="font-mono-caption text-ink-soft mb-3">Zones d'intervention</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight text-anthracite">
                Une entreprise de rénovation<br /><span className="italic text-bordeaux-700">près de chez vous.</span>
              </h2>
              <p className="mt-5 max-w-xl text-ink-soft leading-relaxed">
                Basés à Villepinte, nous intervenons à Paris, dans toute la Seine-Saint-Denis, à l'est du Val-d'Oise
                et au nord-ouest de la Seine-et-Marne. Déplacement et devis gratuits.
              </p>
            </div>
            <Link to="/blog/zones-intervention" data-testid="link-zones" className="btn-ghost self-start">Toutes les zones <ArrowRight size={16} /></Link>
          </div>
          <ul className="flex flex-wrap gap-2">
            {[...TERRITOIRES, ...VILLES].map((z) => (
              <li key={z.slug}>
                <Link
                  to={zoneUrl(z.slug)}
                  data-testid={`home-zone-${z.slug}`}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-black/12 rounded-full text-sm text-anthracite hover:border-bordeaux-700 hover:text-bordeaux-700 transition-colors"
                >
                  <MapPin size={13} className="text-bordeaux-700" /> Rénovation {z.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* =========================================
          GUIDES & PRIX
          ========================================= */}
      <section data-testid="home-guides" className="section reveal">
        <div className="wrap">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
            <div>
              <div className="font-mono-caption text-ink-soft mb-3">Blog — Guides & conseils</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight text-anthracite">
                Comprendre vos travaux<br /><span className="italic text-bordeaux-700">avant de vous lancer.</span>
              </h2>
            </div>
            <Link to="/blog" data-testid="link-guides" className="btn-ghost self-start">Tous les guides <ArrowRight size={16} /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {GUIDES.slice(0, 3).map((g) => (
              <Link key={g.slug} to={guideUrl(g.slug)} data-testid={`home-guide-${g.slug}`} className="group flex flex-col overflow-hidden rounded-rama-md border border-black/10 bg-white hover:border-bordeaux-700 transition-colors">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={g.img} alt={g.h1} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <div className="font-mono-caption text-bordeaux-700 mb-3">{g.kicker} · {g.readTime}</div>
                  <div className="font-display text-xl text-anthracite leading-tight">{g.h1}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          CTA FINAL
          ========================================= */}
      <section className="section bg-anthracite text-ivory reveal">
        <div className="wrap grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="font-mono-caption text-greige mb-4">Prêt à commencer ?</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-tight">Votre projet commence <span className="italic text-greige">ici.</span></h2>
            <p className="mt-4 text-white/70 max-w-lg">Une première estimation en quelques minutes, puis une visite technique avec un conducteur de travaux RAMA DÉCO.</p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <button onClick={scrollToEstimator} data-testid="cta-final-estimer" className="btn-primary justify-center">Estimer mon projet <ArrowRight size={16} /></button>
            <a href="tel:0627443230" data-testid="cta-final-appeler" className="btn-ghost btn-ghost-light justify-center">Appeler 06 27 44 32 30</a>
          </div>
        </div>
      </section>
    </div>
  );
}
